import { FileText, Loader2, Search, Sparkles, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import feathers from '../../feathers';
import { Document } from '../../models/document';
import { startProgress } from '../../redux/slices/simpleLegalProgressSlice';
import {
    openViewer,
    setQueryText,
    setSearchStatus,
    setSelectedDocumentIds,
    startSearching
} from '../../redux/slices/userDocumentSearchSlice';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { pollProgressFromDB } from '../../redux/thunks/SimpleMQTTThunks';

interface SearchDocumentModalProps {
    open: boolean;
    onClose: () => void;
    allDocuments: Document[];
}

export default function SearchDocumentModal({ open, onClose, allDocuments }: SearchDocumentModalProps) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const {
        queryText,
        selectedDocumentIds,
        isSearchAll,
        isSearching,
        statusMessage,
        currentQueryId
    } = useAppSelector(state => state.userDocumentSearch);

    const currentCaseId = useAppSelector(state => state.CaseReducer.currentCaseId);
    const activeProgress = useAppSelector(state => currentQueryId ? state.simpleLegalProgress.activeProgresses[currentQueryId] : null);

    useEffect(() => {
        if (!activeProgress) return;
        if (activeProgress.stageDetails) {
            dispatch(setSearchStatus(activeProgress.stageDetails));
        }
        if (activeProgress.status === 'completed') {
            fetchResults();
        }
    }, [activeProgress]);

    useEffect(() => {
        let pollInterval: any;
        if (isSearching && currentQueryId && (!activeProgress || activeProgress.status === 'active')) {
            pollInterval = setInterval(async () => {
                try {
                    const result = await dispatch(pollProgressFromDB({
                        queryId: currentQueryId,
                        servicePath: 'case-documents-search'
                    })).unwrap();
                    if (result === true) {
                        fetchResults();
                        clearInterval(pollInterval);
                    }
                } catch (error) {
                    console.error('Polling error:', error);
                }
            }, 3000);
        }
        return () => clearInterval(pollInterval);
    }, [isSearching, currentQueryId, activeProgress?.status]);

    const fetchResults = async () => {
        if (!currentQueryId) return;
        try {
            const response = await feathers.service('case-documents-search').find({
                query: { 'metadata.queryId': currentQueryId }
            });
            const responseData = (response as any).data || response;
            if (responseData && responseData.length > 0) {
                const searchRecord = responseData[0];
                if (searchRecord.status === 'completed') {
                    dispatch(openViewer({ results: searchRecord.results || [], query: queryText }));
                } else {
                    setTimeout(fetchResults, 2000);
                }
            }
        } catch (error) {
            console.error('Error fetching results:', error);
        }
    };

    const handleToggleDocument = (id: string) => {
        const nextIds = selectedDocumentIds.includes(id)
            ? selectedDocumentIds.filter(i => i !== id)
            : [...selectedDocumentIds, id];
        dispatch(setSelectedDocumentIds(nextIds));
    };

    const handleSelectAll = () => {
        if (selectedDocumentIds.length === allDocuments.length) {
            dispatch(setSelectedDocumentIds([]));
        } else {
            dispatch(setSelectedDocumentIds(allDocuments.map(d => d._id!)));
        }
    };

    const handleSearch = async () => {
        if (!queryText.trim() || selectedDocumentIds.length === 0 || !currentCaseId) return;
        const newQueryId = `search_${Date.now()}`;
        dispatch(startSearching({ queryId: newQueryId }));
        dispatch(startProgress({ queryId: newQueryId, caseId: currentCaseId, chatId: 'document-search' }));
        try {
            const kbIds = [...new Set(allDocuments.filter(d => selectedDocumentIds.includes(d._id!)).map(d => d.knowledgebaseId))];
            await feathers.service('actions').create({
                cmd: 'user-document-search',
                queryText,
                caseId: currentCaseId,
                selectedKnowledgebaseIds: kbIds,
                selectedDocumentIds,
                queryId: newQueryId,
                language: 'en'
            });
        } catch (error) {
            console.error('Search failed:', error);
            dispatch(setSearchStatus('Search failed to start.'));
        }
    };

    const canClose = !isSearching;

    return (
        <Dialog open={open} onOpenChange={(o) => !o && canClose && onClose()}>
            <DialogContent className="max-w-lg bg-card border-border rounded-2xl p-0 overflow-hidden gap-0">
                {/* Header */}
                <div className="flex items-center justify-between p-5 pb-4 border-b border-border">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 rounded-xl bg-gold/10 border border-gold/20 text-gold">
                            <Sparkles size={22} />
                        </div>
                        <div>
                            <DialogTitle className="text-base font-black tracking-tight text-foreground">
                                {t('knowledgebase.smartDocumentSearch', 'Smart Document Search')}
                            </DialogTitle>
                            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mt-0.5">
                                {t('knowledgebase.aiPoweredAnalysis', 'AI-POWERED CASE ANALYSIS')}
                            </p>
                        </div>
                    </div>
                    {canClose && (
                        <button onClick={onClose} className="p-1.5 rounded-lg text-muted-foreground hover:bg-secondary transition-colors">
                            <X size={16} />
                        </button>
                    )}
                </div>

                <div className="p-5 space-y-5">
                    {/* Query textarea */}
                    <div>
                        <label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest flex items-center gap-1.5 mb-2">
                            <Search size={12} className="text-gold" />
                            {t('knowledgebase.searchQuery', 'ANALYTICAL QUERY')}
                        </label>
                        <Textarea
                            rows={4}
                            placeholder={t('knowledgebase.searchPlaceholder', 'e.g., Extract all liability clauses and identify any potential risks...')}
                            value={queryText}
                            onChange={(e) => dispatch(setQueryText(e.target.value))}
                            disabled={isSearching}
                            className="resize-none bg-background border-border focus:border-gold focus:ring-gold/20 text-sm font-medium placeholder:text-muted-foreground/40"
                        />
                    </div>

                    {/* Document scope */}
                    {(isSearchAll || selectedDocumentIds.length > 0) && (
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
                                    <FileText size={12} className="text-gold" />
                                    {t('knowledgebase.scopeOfAnalysis', 'SCOPE OF ANALYSIS')}
                                </label>
                                {allDocuments.length > 1 && (
                                    <button
                                        onClick={handleSelectAll}
                                        className="text-xs font-bold text-gold hover:text-gold-bright transition-colors"
                                    >
                                        {selectedDocumentIds.length === allDocuments.length
                                            ? t('common.deselectAll', 'Deselect All')
                                            : t('common.selectAll', 'Select All')}
                                    </button>
                                )}
                            </div>

                            <div className="max-h-44 overflow-y-auto rounded-xl border border-border bg-background p-2 space-y-0.5">
                                {allDocuments.map((doc) => {
                                    const checked = selectedDocumentIds.includes(doc._id!);
                                    return (
                                        <label
                                            key={doc._id}
                                            className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg cursor-pointer hover:bg-gold/5 transition-colors"
                                        >
                                            <Checkbox
                                                checked={checked}
                                                onCheckedChange={() => handleToggleDocument(doc._id!)}
                                                className="data-[state=checked]:bg-gold data-[state=checked]:border-gold"
                                            />
                                            <FileText size={13} className={checked ? 'text-gold' : 'text-muted-foreground'} />
                                            <span className={`text-sm truncate max-w-[300px] ${checked ? 'font-bold text-foreground' : 'font-medium text-muted-foreground'}`}>
                                                {doc.name}
                                            </span>
                                        </label>
                                    );
                                })}
                            </div>

                            <div className="mt-2">
                                <Badge className="bg-gold/5 text-gold border-none px-3 py-1 font-bold text-[10px] uppercase tracking-wider hover:bg-gold/10">
                                    {selectedDocumentIds.length} {t('knowledgebase.documentsIncluded', 'Documents Included')}
                                </Badge>
                            </div>
                        </div>
                    )}

                    {/* Progress */}
                    {isSearching && (
                        <div className="py-3 text-center space-y-3">
                            <p className="text-xs font-black text-gold tracking-widest uppercase">
                                {statusMessage}
                            </p>
                            <div className="w-full h-2 bg-secondary rounded-full overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-gold to-gold-bright rounded-full animate-pulse" style={{ width: '60%' }} />
                            </div>
                            <p className="text-xs text-muted-foreground italic">
                                {t('knowledgebase.thinkingTime', 'Large documents may take a few moments to analyze...')}
                            </p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-2 px-5 pb-5">
                    <Button
                        variant="ghost"
                        onClick={onClose}
                        disabled={isSearching}
                        className="text-muted-foreground hover:text-foreground"
                    >
                        {t('common.cancel', 'Cancel')}
                    </Button>
                    <Button
                        onClick={handleSearch}
                        disabled={isSearching || !queryText.trim() || selectedDocumentIds.length === 0}
                        className="gap-2 bg-gold hover:bg-gold-bright text-white border-none shadow-md px-6 font-bold"
                    >
                        {isSearching ? (
                            <><Loader2 size={16} className="animate-spin" />{t('common.analyzing', 'ANALYZING...')}</>
                        ) : (
                            <><Sparkles size={16} />{t('common.startAnalysis', 'START ANALYSIS')}</>
                        )}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
